import React, {
  memo, useMemo, useRef, useState,
} from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import {
  CheckCircleOutline,
  CloudUpload,
  ErrorOutline,
  ExpandMore,
  InsertDriveFile,
  Warning,
} from '@material-ui/icons';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import {
  buildImportedRelations,
  CSV_DELIMITERS,
  getImportColumns,
  GROUP_SEPARATORS,
  inferImportShape,
  MAX_IMPORT_FILE_BYTES,
  parseSynonymCsv,
  planSynonymImport,
} from '../import/synonymCsvImport';
import styles from '../Synonyms.module.scss';

const EMPTY_BUILD = {
  relations: [],
  issues: [],
  stats: {
    sourceRows: 0, validRelations: 0, invalidRows: 0, duplicates: 0, conflicts: 0,
  },
};

const ISSUE_KEYS = Object.freeze({
  csv_parse_error: 'search.synonyms.import.issue.parse',
  too_many_rows: 'search.synonyms.import.issue.tooManyRows',
  too_many_relations: 'search.synonyms.import.issue.tooManyRelations',
  missing_term: 'search.synonyms.import.issue.missingTerm',
  empty_row: 'search.synonyms.import.issue.emptyRow',
  same_term: 'search.synonyms.import.issue.sameTerm',
  control_character: 'search.synonyms.import.issue.controlCharacter',
  term_too_long: 'search.synonyms.import.issue.termTooLong',
  group_needs_two_terms: 'search.synonyms.import.issue.groupNeedsTwo',
  duplicate_in_file: 'search.synonyms.import.issue.duplicateFile',
  direction_conflict_in_file: 'search.synonyms.import.issue.directionFile',
  duplicate_existing: 'search.synonyms.import.issue.duplicateExisting',
  direction_conflict_existing: 'search.synonyms.import.issue.directionExisting',
  ambiguous_source_existing: 'search.synonyms.import.issue.ambiguousSource',
});

const IMPORT_SELECT_PROPS = {
  className: styles.importSelect,
  IconComponent: ExpandMore,
  MenuProps: {
    classes: { paper: styles.importSelectMenu },
    MenuListProps: { className: styles.importSelectMenuList },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  },
};

const SummaryCard = ({ tone, value, label, icon }) => (
  <div className={`${styles.importSummaryCard} ${styles[`importSummary${tone}`]}`}>
    <span className={styles.importSummaryIcon}>{icon}</span>
    <span>
      <strong>{value}</strong>
      <small>{label}</small>
    </span>
  </div>
);

const ImportSynonymsDialog = memo(({
  open,
  onClose,
  onApply,
  existingSynonyms,
}) => {
  const localizeMessage = useLocalizedMessage();
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [fileText, setFileText] = useState('');
  const [fileError, setFileError] = useState('');
  const [delimiter, setDelimiter] = useState('auto');
  const [hasHeader, setHasHeader] = useState(true);
  const [shape, setShape] = useState('pair');
  const [sourceColumn, setSourceColumn] = useState(0);
  const [targetColumn, setTargetColumn] = useState(1);
  const [groupColumn, setGroupColumn] = useState(0);
  const [groupSeparator, setGroupSeparator] = useState(',');
  const [bidirectional, setBidirectional] = useState(true);
  const [mergeMode, setMergeMode] = useState('add');
  const [isDragging, setIsDragging] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const parsed = useMemo(() => (
    fileText ? parseSynonymCsv(fileText, delimiter) : { rows: [], errors: [], delimiter: ',' }
  ), [delimiter, fileText]);
  const columns = useMemo(() => getImportColumns(
    parsed.rows,
    hasHeader,
    index => localizeMessage('search.synonyms.import.column', { number: index + 1 }),
  ), [hasHeader, localizeMessage, parsed.rows]);
  const mappingValid = columns.length > 0
    && (shape === 'group'
      ? groupColumn < columns.length
      : sourceColumn < columns.length
        && targetColumn < columns.length
        && sourceColumn !== targetColumn);
  const built = useMemo(() => {
    if (!fileText || !mappingValid) return EMPTY_BUILD;
    return buildImportedRelations({
      rows: parsed.rows,
      hasHeader,
      shape,
      sourceColumn,
      targetColumn,
      groupColumn,
      groupSeparator,
      bidirectional,
    });
  }, [
    bidirectional,
    fileText,
    groupColumn,
    groupSeparator,
    hasHeader,
    mappingValid,
    parsed.rows,
    shape,
    sourceColumn,
    targetColumn,
  ]);
  const plan = useMemo(() => planSynonymImport(
    existingSynonyms,
    built.relations,
    mergeMode,
  ), [built.relations, existingSynonyms, mergeMode]);
  const allIssues = useMemo(
    () => [...parsed.errors, ...built.issues, ...plan.issues],
    [built.issues, parsed.errors, plan.issues],
  );
  const actionCount = plan.stats.added + plan.stats.updated;
  const blockingError = Boolean(fileError || parsed.errors.length || !mappingValid);
  const canApply = Boolean(fileText)
    && !blockingError
    && built.relations.length > 0
    && (mergeMode === 'replace'
      ? built.issues.length === 0
      : actionCount > 0);

  const clearFile = () => {
    setFileName('');
    setFileText('');
    setFileError('');
    setDelimiter('auto');
    setHasHeader(true);
    setShape('pair');
    setSourceColumn(0);
    setTargetColumn(1);
    setGroupColumn(0);
    setGroupSeparator(',');
    setBidirectional(true);
    setMergeMode('add');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const closeDialog = () => {
    if (isApplying) return;
    clearFile();
    onClose();
  };

  const loadFile = (file) => {
    if (!file) return;
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      setFileError(localizeMessage('search.synonyms.import.fileTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      if (!text.trim()) {
        setFileName(file.name);
        setFileText('');
        setFileError(localizeMessage('search.synonyms.import.fileEmpty'));
        return;
      }
      const initial = parseSynonymCsv(text, 'auto');
      const inferredShape = inferImportShape(initial.rows, true);
      setFileName(file.name);
      setFileText(text);
      setFileError('');
      setDelimiter('auto');
      setHasHeader(true);
      setShape(inferredShape);
      setSourceColumn(0);
      setTargetColumn(1);
      setGroupColumn(0);
      setGroupSeparator(',');
      setBidirectional(true);
      setMergeMode('add');
    };
    reader.onerror = () => setFileError(localizeMessage('search.synonyms.import.fileReadError'));
    reader.readAsText(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    loadFile(event.dataTransfer.files?.[0]);
  };

  const handleApply = async () => {
    if (!canApply || isApplying) return;
    setIsApplying(true);
    try {
      const applied = await onApply({
        ...plan,
        sourceStats: built.stats,
        fileName,
        mergeMode,
      });
      if (applied !== false) {
        clearFile();
        onClose();
      }
    } finally {
      setIsApplying(false);
    }
  };

  const issueMessage = issue => localizeMessage(
    ISSUE_KEYS[issue.code] || 'search.synonyms.import.issue.unknown',
    issue.values,
  );

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      maxWidth="md"
      fullWidth
      aria-labelledby="synonym-import-title"
      classes={{ paper: styles.importDialogPaper }}
    >
      <DialogTitle id="synonym-import-title" disableTypography className={styles.importDialogTitle}>
        <span>
          <strong>{localizeMessage('search.synonyms.import.title')}</strong>
          <small>{localizeMessage('search.synonyms.import.subtitle')}</small>
        </span>
      </DialogTitle>
      <DialogContent className={styles.importDialogContent}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          hidden
          onClick={(event) => { event.currentTarget.value = ''; }}
          onChange={event => loadFile(event.target.files?.[0])}
          data-testid="synonyms-import-file"
        />

        {!fileText ? (
          <button
            type="button"
            className={`${styles.importDropzone} ${isDragging ? styles.importDropzoneActive : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
            onDragOver={event => event.preventDefault()}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            data-testid="synonyms-import-dropzone"
          >
            <CloudUpload />
            <strong>{localizeMessage('search.synonyms.import.dropTitle')}</strong>
            <span>{localizeMessage('search.synonyms.import.dropHint')}</span>
          </button>
        ) : (
          <>
            <div className={styles.importFileBar}>
              <InsertDriveFile />
              <span>
                <strong>{fileName}</strong>
                <small>{localizeMessage('search.synonyms.import.detected', {
                  rows: parsed.rows.length,
                  delimiter: parsed.delimiter === '\t'
                    ? localizeMessage('search.synonyms.import.delimiter.tab')
                    : parsed.delimiter,
                })}</small>
              </span>
              <Button size="small" onClick={() => fileInputRef.current?.click()}>
                {localizeMessage('search.synonyms.import.changeFile')}
              </Button>
            </div>

            <section className={styles.importSection}>
              <div className={styles.importSectionHeading}>
                <span className={styles.importStep}>1</span>
                <span>
                  <strong>{localizeMessage('search.synonyms.import.configureTitle')}</strong>
                  <small>{localizeMessage('search.synonyms.import.configureHint')}</small>
                </span>
              </div>
              <div className={styles.importConfigGrid}>
                <FormControl variant="outlined" size="small" className={styles.importSelectField}>
                  <InputLabel id="synonym-delimiter-label">
                    {localizeMessage('search.synonyms.import.delimiter.label')}
                  </InputLabel>
                  <Select
                    {...IMPORT_SELECT_PROPS}
                    labelId="synonym-delimiter-label"
                    value={delimiter}
                    onChange={event => setDelimiter(event.target.value)}
                    label={localizeMessage('search.synonyms.import.delimiter.label')}
                    data-testid="synonyms-import-delimiter"
                  >
                    {CSV_DELIMITERS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {localizeMessage(option.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" className={styles.importSelectField}>
                  <InputLabel id="synonym-shape-label">
                    {localizeMessage('search.synonyms.import.shape.label')}
                  </InputLabel>
                  <Select
                    {...IMPORT_SELECT_PROPS}
                    labelId="synonym-shape-label"
                    value={shape}
                    onChange={(event) => {
                      const nextShape = event.target.value;
                      setShape(nextShape);
                    }}
                    label={localizeMessage('search.synonyms.import.shape.label')}
                    data-testid="synonyms-import-shape"
                  >
                    <MenuItem value="pair">
                      {localizeMessage('search.synonyms.import.shape.pair')}
                    </MenuItem>
                    <MenuItem value="group">
                      {localizeMessage('search.synonyms.import.shape.group')}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  className={styles.importHeaderToggle}
                  control={(
                    <Switch
                      checked={hasHeader}
                      color="primary"
                      onChange={event => setHasHeader(event.target.checked)}
                    />
                  )}
                  label={localizeMessage('search.synonyms.import.header')}
                />
              </div>

              <div className={styles.importMappingGrid}>
                {shape === 'pair' ? (
                  <>
                    <FormControl variant="outlined" size="small" className={styles.importSelectField}>
                      <InputLabel id="synonym-source-column-label">
                        {localizeMessage('search.synonyms.import.sourceColumn')}
                      </InputLabel>
                      <Select
                        {...IMPORT_SELECT_PROPS}
                        labelId="synonym-source-column-label"
                        value={sourceColumn}
                        onChange={event => setSourceColumn(Number(event.target.value))}
                        label={localizeMessage('search.synonyms.import.sourceColumn')}
                        aria-describedby={!mappingValid ? 'synonym-mapping-error' : undefined}
                      >
                        {columns.map(column => (
                          <MenuItem key={column.value} value={column.value}>{column.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      variant="outlined"
                      size="small"
                      error={sourceColumn === targetColumn}
                      className={styles.importSelectField}
                    >
                      <InputLabel id="synonym-target-column-label">
                        {localizeMessage('search.synonyms.import.targetColumn')}
                      </InputLabel>
                      <Select
                        {...IMPORT_SELECT_PROPS}
                        labelId="synonym-target-column-label"
                        value={targetColumn < columns.length ? targetColumn : ''}
                        onChange={event => setTargetColumn(Number(event.target.value))}
                        label={localizeMessage('search.synonyms.import.targetColumn')}
                        aria-describedby={!mappingValid ? 'synonym-mapping-error' : undefined}
                      >
                        {columns.map(column => (
                          <MenuItem key={column.value} value={column.value}>{column.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                ) : (
                  <>
                    <FormControl variant="outlined" size="small" className={styles.importSelectField}>
                      <InputLabel id="synonym-group-column-label">
                        {localizeMessage('search.synonyms.import.groupColumn')}
                      </InputLabel>
                      <Select
                        {...IMPORT_SELECT_PROPS}
                        labelId="synonym-group-column-label"
                        value={groupColumn}
                        onChange={event => setGroupColumn(Number(event.target.value))}
                        label={localizeMessage('search.synonyms.import.groupColumn')}
                      >
                        {columns.map(column => (
                          <MenuItem key={column.value} value={column.value}>{column.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={styles.importSelectField}>
                      <InputLabel id="synonym-group-separator-label">
                        {localizeMessage('search.synonyms.import.groupSeparator')}
                      </InputLabel>
                      <Select
                        {...IMPORT_SELECT_PROPS}
                        labelId="synonym-group-separator-label"
                        value={groupSeparator}
                        onChange={event => setGroupSeparator(event.target.value)}
                        label={localizeMessage('search.synonyms.import.groupSeparator')}
                      >
                        {GROUP_SEPARATORS.map(value => (
                          <MenuItem key={value} value={value}>{value}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                <FormControl component="fieldset" className={styles.importDirection}>
                  <FormLabel component="legend">
                    {localizeMessage('search.synonyms.import.direction')}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-label={localizeMessage('search.synonyms.import.direction')}
                    value={bidirectional ? 'both' : 'one'}
                    onChange={event => setBidirectional(event.target.value === 'both')}
                  >
                    <FormControlLabel
                      value="one"
                      control={<Radio color="primary" />}
                      label={localizeMessage('search.synonyms.import.direction.one')}
                    />
                    <FormControlLabel
                      value="both"
                      control={<Radio color="primary" />}
                      label={localizeMessage('search.synonyms.import.direction.both')}
                    />
                  </RadioGroup>
                </FormControl>
              </div>

              {!mappingValid && (
                <FormHelperText id="synonym-mapping-error" error className={styles.importMappingError}>
                  {localizeMessage('search.synonyms.import.mappingInvalid')}
                </FormHelperText>
              )}

              {shape === 'group' && (
                <div className={styles.importGroupHint}>
                  {localizeMessage('search.synonyms.import.groupHint')}
                </div>
              )}
            </section>

            <section className={styles.importSection}>
              <div className={styles.importSectionHeading}>
                <span className={styles.importStep}>2</span>
                <span>
                  <strong>{localizeMessage('search.synonyms.import.previewTitle')}</strong>
                  <small>{localizeMessage('search.synonyms.import.previewHint')}</small>
                </span>
              </div>
              <div className={styles.importSummaryGrid}>
                <SummaryCard
                  tone="Success"
                  value={built.stats.validRelations}
                  label={localizeMessage('search.synonyms.import.summary.valid')}
                  icon={<CheckCircleOutline />}
                />
                <SummaryCard
                  tone="Neutral"
                  value={built.stats.duplicates + plan.stats.duplicates}
                  label={localizeMessage('search.synonyms.import.summary.duplicates')}
                  icon={<InsertDriveFile />}
                />
                <SummaryCard
                  tone="Warning"
                  value={built.stats.conflicts + plan.stats.conflicts}
                  label={localizeMessage('search.synonyms.import.summary.conflicts')}
                  icon={<Warning />}
                />
                <SummaryCard
                  tone="Error"
                  value={built.stats.invalidRows + parsed.errors.length}
                  label={localizeMessage('search.synonyms.import.summary.errors')}
                  icon={<ErrorOutline />}
                />
              </div>

              <div className={styles.importPreviewTable}>
                <Table size="small" aria-label={localizeMessage('search.synonyms.import.previewTitle')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{localizeMessage('search.synonyms.import.preview.line')}</TableCell>
                      <TableCell>{localizeMessage('search.synonyms.table.left')}</TableCell>
                      <TableCell>{localizeMessage('search.synonyms.import.preview.relation')}</TableCell>
                      <TableCell>{localizeMessage('search.synonyms.table.right')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {built.relations.slice(0, 8).map((relation, index) => (
                      <TableRow key={`${relation.sourceLine}-${index}`}>
                        <TableCell>{relation.sourceLine}</TableCell>
                        <TableCell>{relation.left.join(', ')}</TableCell>
                        <TableCell>{relation.bidirectional ? '↔' : '→'}</TableCell>
                        <TableCell>{relation.right.join(', ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {built.relations.length > 8 && (
                  <small className={styles.importPreviewLimit}>
                    {localizeMessage('search.synonyms.import.previewLimited', {
                      shown: 8,
                      total: built.relations.length,
                    })}
                  </small>
                )}
              </div>

              {allIssues.length > 0 && (
                <div className={styles.importIssues} role="status">
                  <strong>{localizeMessage('search.synonyms.import.issuesTitle')}</strong>
                  <ul>
                    {allIssues.slice(0, 8).map((issue, index) => (
                      <li key={`${issue.line}-${issue.code}-${index}`}>
                        {issue.line > 0
                          ? `${localizeMessage('search.synonyms.import.preview.line')} ${issue.line}: `
                          : ''}
                        {issueMessage(issue)}
                      </li>
                    ))}
                  </ul>
                  {allIssues.length > 8 && (
                    <small>{localizeMessage('search.synonyms.import.moreIssues', {
                      count: allIssues.length - 8,
                    })}</small>
                  )}
                </div>
              )}
            </section>

            <section className={styles.importSection}>
              <div className={styles.importSectionHeading}>
                <span className={styles.importStep}>3</span>
                <span>
                  <strong>{localizeMessage('search.synonyms.import.modeTitle')}</strong>
                  <small>{localizeMessage('search.synonyms.import.modeHint')}</small>
                </span>
              </div>
              <RadioGroup
                value={mergeMode}
                aria-label={localizeMessage('search.synonyms.import.modeTitle')}
                onChange={event => setMergeMode(event.target.value)}
                className={styles.importModes}
              >
                {['add', 'update', 'replace'].map(mode => (
                  <label
                    key={mode}
                    className={`${styles.importModeCard} ${mergeMode === mode ? styles.importModeSelected : ''}`}
                  >
                    <Radio value={mode} color="primary" />
                    <span>
                      <strong>{localizeMessage(`search.synonyms.import.mode.${mode}`)}</strong>
                      <small>{localizeMessage(`search.synonyms.import.mode.${mode}Hint`)}</small>
                    </span>
                  </label>
                ))}
              </RadioGroup>

              <div className={styles.importPlanSummary}>
                {localizeMessage('search.synonyms.import.plan', {
                  added: plan.stats.added,
                  updated: plan.stats.updated,
                  skipped: plan.stats.skipped,
                  removed: plan.stats.removed,
                })}
              </div>
            </section>
          </>
        )}

        {fileError && <div className={styles.importFileError} role="alert">{fileError}</div>}
      </DialogContent>
      <DialogActions className={styles.importDialogActions}>
        <Button onClick={closeDialog} disabled={isApplying}>
          {localizeMessage('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canApply || isApplying}
          onClick={handleApply}
          data-testid="synonyms-import-apply"
        >
          {isApplying && <CircularProgress size={15} className={styles.importApplyProgress} />}
          {localizeMessage('search.synonyms.import.apply', { count: actionCount })}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ImportSynonymsDialog;
